import { useMemo } from 'react';
import type { GridCell, ZoneType } from '../../types/core';
import { coordToWorld } from '../../utils/grid';
import { useGameStore } from '../../simulation/store';
import { palette, zoneColour } from '../materials';

function Tile({ cell, size, selectedTool }: { readonly cell: GridCell; readonly size: number; readonly selectedTool: string }) {
  const beginDrag = useGameStore((state) => state.beginDrag);
  const updateDrag = useGameStore((state) => state.updateDrag);
  const setHover = useGameStore((state) => state.setHover);
  const selectBuilding = useGameStore((state) => state.selectBuilding);
  const position = coordToWorld(cell.coord, size);
  const colour = cell.road
    ? cell.roadEdge
      ? palette.roadEdge
      : palette.road
    : cell.zone
      ? zoneColour(cell.zone)
      : cell.treeSeed > 0.72
        ? palette.grassB
        : palette.grassA;

  return (
    <mesh
      receiveShadow
      position={[position.x, cell.terrainHeight, position.z]}
      rotation={[-Math.PI / 2, 0, 0]}
      onPointerDown={(event) => {
        event.stopPropagation();
        if (selectedTool === 'residential' || selectedTool === 'commercial' || selectedTool === 'workspace' || selectedTool === 'park') {
          beginDrag(selectedTool as ZoneType, cell.coord);
        }
        if (cell.buildingId) {
          selectBuilding(cell.buildingId);
        }
      }}
      onPointerOver={(event) => {
        event.stopPropagation();
        updateDrag(cell.coord);
        setHover({ cell: cell.coord, buildingId: cell.buildingId });
      }}
    >
      <planeGeometry args={[0.98, 0.98]} />
      <meshStandardMaterial color={colour} roughness={0.92} metalness={0.02} />
    </mesh>
  );
}

export function TerrainGrid() {
  const cells = useGameStore((state) => state.world.cells);
  const size = useGameStore((state) => state.world.size);
  const selectedTool = useGameStore((state) => state.selectedTool);
  const tiles = useMemo(() => cells, [cells]);

  return (
    <group>
      {tiles.map((cell) => (
        <Tile key={cell.key} cell={cell} size={size} selectedTool={selectedTool} />
      ))}
    </group>
  );
}
